// Vanity URL redirects.
//
// These are clean, stable public URLs used in ads / organic posts. The redirect
// sends the visitor to the real destination (the site's own /signup with fixed
// affiliate tracking params) while keeping the advertised URL stable, so the
// destination can be changed here without editing the ads.
//
// IMPORTANT: this runs in src that is shared (symlinked) across every site, so
// the map is keyed by the site's bare domain. Matching is done against the
// current window.location.hostname, which is `golf.singles` (prod apex),
// `www.golf.singles` (prod), `dev.golf.singles` (dev), `staging.golf.singles`,
// etc. We do NOT key off config.DOMAIN: makeConfig() rewrites that to a full URL
// per environment (e.g. 'https://dev.golf.singles'), so it is not the bare host.
// Only the domains listed here are affected; every other site is a no-op.
//
// The redirect is intentionally a full-page navigation (window.location.replace
// in index.js), not a client-side React <Redirect>. Affiliate params (aff_*) are
// parsed only once, on full page load, in MainIndexContainer. A client-side
// redirect would mount the app on the param-less vanity URL first, so the params
// added by the redirect would never be picked up -> broken attribution. A hard
// navigation reproduces the known-good "ad link straight to /signup" path.
//
// Targets are RELATIVE (start with '/') on purpose: window.location.replace
// resolves them against the current origin, so the redirect stays in the same
// environment (dev -> dev, prod -> prod).
//
// Per Frank (2026-06-03): any query params on the incoming vanity URL are
// IGNORED -- we always redirect to the fixed hardcoded target.

export const VANITY_REDIRECTS = {
  'golf.singles': {
    '/browsesingles':
      '/signup?aff_id=facebookppu&aff_pg=PPU&gender=man&seeking=woman&aff_cp=ThePages-GolfSingles&aff_adg=otc26',
  },
  'bikers.dating': {
    '/browsesingles':
      '/signup?aff_id=facebookppu&aff_pg=PPU&gender=man&seeking=woman&aff_cp=ThePages-BikersDating&aff_adg=otc26',
  },
};

/**
 * Resolve the fixed redirect target for a vanity URL, or null if the current
 * host + path is not a vanity URL.
 *
 * Pure function (no navigation side effects) so it can be unit tested. The
 * caller performs the actual window.location.replace().
 *
 * The host is matched against each configured bare domain, accepting the apex
 * (golf.singles) or any subdomain of it (www./dev./staging.golf.singles). Any
 * query string on the incoming vanity URL is intentionally ignored; we always
 * return the hardcoded target (per Frank, 2026-06-03).
 *
 * @param {string} hostname - window.location.hostname (e.g. 'dev.golf.singles')
 * @param {string} pathname - window.location.pathname
 * @returns {string|null} relative destination path, or null when not a vanity URL
 */
export function getVanityRedirectTarget(hostname, pathname) {
  const host = (hostname || '').toLowerCase();
  const domainKey = Object.keys(VANITY_REDIRECTS).find(
    (d) => host === d || host.endsWith('.' + d)
  );
  if (!domainKey) {
    return null;
  }

  const map = VANITY_REDIRECTS[domainKey];
  const normalizedPath = (pathname || '').replace(/\/+$/, '').toLowerCase() || '/';
  return map[normalizedPath] || null;
}
